import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Button as MuiButton,
} from "@mui/material";
import { useAuth } from "../../auth/AuthContext";
import Footer from "../../pages/home/Footer";
import bgimage from "../../assets/support/support.jpg";
import { useSearchParams } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const ProjectSupport = () => {
  const { sendMails, getData, addData } = useAuth();
  const [background, setBackground] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [project, setProject] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState("");
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    userType: "individual",
    user_email: "",
    user_cell: "",
    nationality: "",
    supportType: "money",
    ammount_paid: "",
    goods_given: "",
    payment_method: "Card",
    image: null,
    projectId: "",
  });

  // Get projectId from URL
  const projectId = searchParams.get("projectId");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Fetch project details using the projectId from URL
        if (projectId) {
          const responseProject = await getData("proccess.php", "projects_V2");

          if (responseProject.success) {
            const proj = responseProject.data.find(p => p.id == projectId);
            
            if (proj) {
              setProject(proj);
              
              // Update form data with project information
              setFormData(prev => ({
                ...prev,
                projectId: projectId,
              }));
            } else {
              setErrors({ general: "Project not found. Please check the URL." });
            }
          } else {
            setErrors({ general: "Failed to load project data." });
          }
        } else {
          setErrors({ general: "No project specified. Please use a valid project support link." });
        }

        // Fetch background
        const response = await getData("proccess.php", "backgrounds");
        if (response.success) {
          const contactbg = response.data.find(
            (bg) => bg.page_name === "donation"
          );
          if (contactbg) {
            setBackground(contactbg);
          }
        }
      } catch (error) {
        setErrors({ general: error.message });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [getData, projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          proofFile: "Please upload a PDF, JPG, PNG, or GIF file",
        }));
        e.target.value = ""; // Clear the input
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          proofFile: "File size should be less than 5MB",
        }));
        e.target.value = ""; // Clear the input
        return;
      }

      // Set the file directly to the image field
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
      setFileName(file.name);
      if (errors.proofFile) {
        setErrors((prev) => ({ ...prev, proofFile: "" }));
      }
    }
  };

  const handleFileButtonClick = () => {
    // Programmatically trigger the hidden file input
    document.getElementById('proof-of-payment-input').click();
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setFileName("");
    // Clear the file input value
    const fileInput = document.getElementById('proof-of-payment-input');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.user_email.trim()) {
      newErrors.user_email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.user_email)) {
      newErrors.user_email = "Email is invalid";
    }
    if (!formData.user_cell.trim())
      newErrors.user_cell = "Phone number is required";
    if (!formData.nationality.trim())
      newErrors.nationality = "Nationality is required";

    if (formData.supportType === "money") {
      if (!formData.ammount_paid || Number(formData.ammount_paid) <= 0) {
        newErrors.ammount_paid = "Amount must be greater than 0";
      }

      // Validate payment method for money donations
      if (formData.payment_method === "EFT" && !formData.image) {
        newErrors.proofFile = "Proof of payment is required for EFT transfers";
      }
    } else {
      if (!formData.goods_given.trim()) {
        newErrors.goods_given = "Please describe the goods you're donating";
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      // Submit to database using FormData
      const response = await addData('proccess.php', 'support_V2', formData);

      if (response.success) {
        setSuccess(true);
        // Reset form (except project info)
        setFormData({
          firstName: "",
          lastName: "",
          userType: "individual",
          user_email: "",
          user_cell: "",
          nationality: "",
          supportType: "money",
          ammount_paid: "",
          goods_given: "",
          payment_method: "Card",
          image: null,
          projectId: projectId,
        });
        setFileName("");

        // Send email notification if needed
        // if (sendMails) {
        //   await sendMails({
        //     to: "admin@engenete.com",
        //     subject: `New Project Support - ${formData.projectName}`,
        //     message: `New project support received from ${formData.firstName} ${formData.lastName}. 
        //               Project: ${formData.projectName}
        //               Type: ${formData.supportType}
        //               Amount/Goods: ${formData.supportType === "money"
        //                 ? `N$${formData.ammount_paid}`
        //                 : formData.goods_given
        //               }
        //               Payment Method: ${formData.payment_method}
        //               Email: ${formData.user_email}
        //               Phone: ${formData.user_cell}
        //               Nationality: ${formData.nationality}
        //               ${formData.payment_method === "EFT" ? "Proof of payment uploaded" : ""}`,
        //   });
        // }
      } else {
        setErrors({ general: response.message || "Failed to submit support" });
      }
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        width={"100%"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        justifyContent={"center"}
        paddingTop={"4rem"}
        sx={{ backgroundColor: background.color || "#e7f1f5", paddingTop: '4rem' }}
      >
        <Box
          width={"100%"}
          minHeight={'30vh'}
          sx={{
            backgroundImage: `url(${bgimage})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <Box
            width={'100%'}
            minHeight={'30vh'}
            sx={{
              backgroundColor: '#ffffffab',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              px: 3
            }}
          >
            <h1 className="primary-header">
              Support {formData.projectName || "This Project"}
            </h1>
            <Typography variant="h6" color="text.secondary">
              Your contribution helps make a difference
            </Typography>
          </Box>
        </Box>
        <Box maxWidth="1200px" width="100%" px={3} margin={"2rem 0"}>
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Thank you for supporting {formData.projectName}! We've received your submission and
              will contact you shortly.
            </Alert>
          )}

          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          {!projectId && !loading && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              No project specified. Please use a valid project support link.
            </Alert>
          )}

          <Box width={"100%"} display={"flex"} flexWrap={"wrap"} gap={"1rem"}>
            {/* Form Section - Left */}
            <Box flex={1} minWidth={"300px"}>
              <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight="medium">
                  Support {formData.projectName}
                </Typography>

                {project && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Project:</strong> {project.project_name}
                    {project.description && (
                      <><br />{project.description.substring(0, 150)}...</>
                    )}
                  </Typography>
                )}

                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  <Box>
                    {/* User Type */}
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          You are donating as:
                        </FormLabel>
                        <RadioGroup
                          row
                          name="userType"
                          value={formData.userType}
                          onChange={handleChange}
                        >
                          <FormControlLabel
                            value="individual"
                            control={<Radio />}
                            label="Individual"
                          />
                          <FormControlLabel
                            value="organisation"
                            control={<Radio />}
                            label="Organization"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Name Fields */}
                    <Grid
                      width={"100%"}
                      minWidth={"250px"}
                      marginBottom={".5rem"}
                    >
                      <TextField
                        fullWidth
                        label="First Name *"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                      />
                    </Grid>
                    <Grid
                      width={"100%"}
                      minWidth={"250px"}
                      marginBottom={".5rem"}
                    >
                      <TextField
                        fullWidth
                        label="Last Name *"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                      />
                    </Grid>

                    {/* Contact Information */}
                    <Box
                      width={"100%"}
                      display={"flex"}
                      flexWrap={"wrap"}
                      gap={"1rem"}
                      marginBottom={".5rem"}
                    >
                      <Grid flex={1} minWidth={"200px"}>
                        <TextField
                          fullWidth
                          label="Email *"
                          name="user_email"
                          type="email"
                          value={formData.user_email}
                          onChange={handleChange}
                          error={!!errors.user_email}
                          helperText={errors.user_email}
                        />
                      </Grid>
                      <Grid flex={1} minWidth={"200px"}>
                        <TextField
                          fullWidth
                          label="Phone Number *"
                          name="user_cell"
                          value={formData.user_cell}
                          onChange={handleChange}
                          error={!!errors.user_cell}
                          helperText={errors.user_cell}
                        />
                      </Grid>

                      <Grid flex={1} minWidth={"200px"}>
                        <TextField
                          fullWidth
                          label="Nationality *"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleChange}
                          error={!!errors.nationality}
                          helperText={errors.nationality}
                        />
                      </Grid>
                    </Box>

                    {/* Support Type */}
                    <Grid item xs={12} marginBottom={".5rem"}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Type of Support *
                        </FormLabel>
                        <RadioGroup
                          row
                          name="supportType"
                          value={formData.supportType}
                          onChange={handleChange}
                        >
                          <FormControlLabel
                            value="money"
                            control={<Radio />}
                            label="Monetary Donation"
                          />
                          <FormControlLabel
                            value="goods"
                            control={<Radio />}
                            label="Goods/Items Donation"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Amount or Goods */}
                    {formData.supportType === "money" ? (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Amount (N$) *"
                          name="ammount_paid"
                          type="number"
                          value={formData.ammount_paid}
                          onChange={handleChange}
                          error={!!errors.ammount_paid}
                          helperText={errors.ammount_paid}
                          InputProps={{
                            startAdornment: <Typography mr={1}>N$</Typography>,
                          }}
                          sx={{ mb: 2 }}
                        />

                        {/* Payment Method */}
                        <FormControl component="fieldset" sx={{ mb: 3 }}>
                          <FormLabel component="legend">
                            Payment Method *
                          </FormLabel>
                          <RadioGroup
                            name="payment_method"
                            value={formData.payment_method}
                            onChange={handleChange}
                          >
                            <FormControlLabel
                              value="Card"
                              control={<Radio />}
                              label="Credit/Debit Card (Coming Soon)"
                              disabled
                            />
                            <FormControlLabel
                              value="EFT"
                              control={<Radio />}
                              label="Electronic Funds Transfer"
                            />
                          </RadioGroup>
                        </FormControl>

                        {/* EFT Proof Upload */}
                        {formData.payment_method === "EFT" && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Upload Proof of Payment *
                            </Typography>
                            <Typography variant="caption" color="text.secondary" paragraph>
                              Please upload a screenshot or PDF of your bank transfer confirmation
                              (PDF, JPG, PNG, GIF - Max 5MB)
                            </Typography>

                            {/* Hidden file input */}
                            <input
                              id="proof-of-payment-input"
                              type="file"
                              name="image"
                              accept=".pdf,.jpg,.jpeg,.png,.gif"
                              onChange={handleFileChange}
                              style={{ display: 'none' }}
                            />

                            {!formData.image ? (
                              <MuiButton
                                variant="outlined"
                                fullWidth
                                onClick={handleFileButtonClick}
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                  py: 2,
                                  borderStyle: 'dashed',
                                  borderColor: errors.proofFile ? 'error.main' : 'grey.400',
                                  mb: 1
                                }}
                              >
                                Click to Upload Proof
                              </MuiButton>
                            ) : (
                              <Box
                                sx={{
                                  p: 2,
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  mb: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AttachFileIcon fontSize="small" />
                                  <Box>
                                    <Typography variant="body2">
                                      {fileName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formData.image ? `${(formData.image.size / 1024).toFixed(2)} KB` : ''}
                                    </Typography>
                                  </Box>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={removeFile}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            )}

                            {errors.proofFile && (
                              <Typography color="error" variant="caption">
                                {errors.proofFile}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Payment Info Alert */}
                        {formData.payment_method === "Card" && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Card payment gateway is currently under development. Please use EFT transfer for now.
                          </Alert>
                        )}
                      </Grid>
                    ) : (
                      <Grid item xs={12} marginBottom={"1rem"}>
                        <TextField
                          fullWidth
                          label="Describe the goods you're donating *"
                          name="goods_given"
                          value={formData.goods_given}
                          onChange={handleChange}
                          error={!!errors.goods_given}
                          helperText={errors.goods_given}
                          multiline
                          rows={3}
                          placeholder="Please describe the items, quantity, and condition..."
                        />
                      </Grid>
                    )}

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <MuiButton
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={submitting || !projectId}
                        sx={{
                          backgroundColor: "#3b76b2",
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: "#2a5a8a"
                          }
                        }}
                      >
                        {submitting ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          `Support ${formData.projectName || "Project"}`
                        )}
                      </MuiButton>
                    </Grid>
                  </Box>
                </form>
              </Paper>
            </Box>

            {/* Bank Details & Project Info Section - Right */}
            <Box flex={1} maxWidth={"300px"}>
              <Card sx={{ position: "sticky", top: "100px" }}>
                <CardContent>
                  {project && (
                    <>
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="medium"
                        color="primary"
                      >
                        Project Information
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        <strong>Project:</strong> {project.project_name}
                      </Typography>
                      {project.description && (
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {project.description.substring(0, 150)}...
                        </Typography>
                      )}
                      <Divider sx={{ my: 2 }} />
                    </>
                  )}

                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight="medium"
                    color="primary"
                  >
                    Bank Transfer Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For direct bank transfers, please use the following details:
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Bank Name:
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Standard Bank
                    </Typography>

                    <Typography variant="subtitle2" fontWeight="bold">
                      Account Name:
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Engenete Investments cc
                    </Typography>

                    <Typography variant="subtitle2" fontWeight="bold">
                      Account Number:
                    </Typography>
                    <Typography variant="body1" paragraph>
                      60003322768
                    </Typography>

                    <Typography variant="subtitle2" fontWeight="bold">
                      Account Type:
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Business Current Account
                    </Typography>

                    <Typography variant="subtitle2" fontWeight="bold">
                      Branch Name:
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Windhoek
                    </Typography>

                    <Typography variant="subtitle2" fontWeight="bold">
                      Branch Code:
                    </Typography>
                    <Typography variant="body1" paragraph>
                      08 2372
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Important:</strong> When using EFT, please:
                      <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                        <li>Use "{project ? project.project_name : 'Project Name'}" and your name as reference</li>
                        <li>Upload proof of payment on this form</li>
                        <li>Keep your transaction receipt for verification</li>
                      </ul>
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default ProjectSupport;